'use client';

import Portal from '@components/Portal/Portal';
import { Flex } from '@coolblue-development/freddy';
import crossSellChargerAddToCartTrigger from '@coolblue-development/ga4-events/triggers/CrossSellToggle/addToCartTrigger';
import orderBlockAddToCartTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/addToCartTrigger';
import orderBlockSummaryAddToCartTrigger from '@coolblue-development/ga4-events/triggers/OrderBlockSummary/addToCartTrigger';
import showSplitTestTrigger from '@coolblue-development/ga4-events/triggers/SplitTest/showSplitTestTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { ATCPostalCodeCheckerModal } from '@coolblue-development/next-postal-code/client';
import { getPostalCode } from '@coolblue-development/next-postal-code/server';
import ReturnNotice from '@features/product/components/DigitalProduct/ReturnNotice';
import { OrderBlockContext } from '@features/product/context';
import { Product, ProductPlusPromotion } from '@features/product/typings';
import type { Store } from '@features/product-page/typings';
import { MainProduct } from '@features/second-chance/typings';
import { ReactNode, RefObject, useContext, useEffect, useRef, useState } from 'react';

import { ProductTradeInContext } from '../../ProductTradeIn/Context';
import { StorePickupButton } from '../../StorePickup';
import { ActiveMailerContextProvider } from '../ActiveMailerButton';
import { useLease } from '../BusinessLeasing/Context';
import RequestLeaseButton from '../BusinessLeasing/RequestLeaseButton';
import { OrderBlockType } from '../OrderBlock.types';
import OrderButton from '../OrderButton';
import QuantityStepper from '../QuantityStepper';
import { StickyAddToCartBar } from '../StickyAddToCartBar';
import { WishlistButton } from '../WishlistButton';
import { onSubmitForm } from './onSubmitForm';
import type { TradeInType } from './types';

type OrderFormProps = {
  product: Product | MainProduct;
  privacyPolicyUrl: string;
  children: ReactNode;
  stickyBarPropositions?: ReactNode;
  availabilityState: ReactNode;
  showStorePickupButton: boolean | undefined;
  isPermanentlyOutOfStock: boolean;
  tradeInComponent?: ReactNode;
  isBusiness: boolean;
  dateOverride?: string;
  stickyBarSalesPrice?: ReactNode;
  isInSinterklaasSplitTest?: boolean;
  isLeasePage?: boolean;
  isPostalCodeCheckerImprovementSplitTestInVariation: boolean;
  shouldDisableAddToCart: boolean;
  orderBlockType: OrderBlockType;
  isSecondChance?: boolean;
  isCrossSellOnly?: boolean;
  secondChanceProductId?: string;
  secondChanceRedirectUrl?: string;
  showAlternativePickUpButtonText?: boolean;
  storeStockElementPDPVariationId?: string;
  customerEmailAddress?: string;
  addToCartRedirectUrl?: string;
  freeWithPromotion?: ProductPlusPromotion;
  preferredStore?: Store;
  loginUrl: string;
  isLoggedIn: boolean;
  showCompactATCButton?: boolean;
  showOrderBlockATCButtonOnMobile?: boolean;
};

export const OrderForm = ({
  product,
  privacyPolicyUrl,
  children,
  stickyBarPropositions,
  availabilityState,
  showStorePickupButton,
  isPermanentlyOutOfStock,
  tradeInComponent,
  isBusiness,
  dateOverride,
  stickyBarSalesPrice,
  isInSinterklaasSplitTest = false,
  isLeasePage,
  isPostalCodeCheckerImprovementSplitTestInVariation,
  shouldDisableAddToCart,
  orderBlockType,
  isSecondChance = false,
  isCrossSellOnly = false,
  secondChanceProductId,
  secondChanceRedirectUrl,
  showAlternativePickUpButtonText,
  storeStockElementPDPVariationId,
  customerEmailAddress,
  addToCartRedirectUrl = undefined,
  freeWithPromotion = undefined,
  preferredStore,
  loginUrl,
  isLoggedIn,
  showCompactATCButton = false,
  showOrderBlockATCButtonOnMobile = false
}: OrderFormProps) => {
  const { trackEvent } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [showStickyAddToCart, setShowStickyAddToCart] = useState(false);
  const [quantity, setQuantity] = useState<number>(1);
  const [isPostalCodeCheckerOpen, setIsPostalCodeCheckerOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { isLegacyLeaseFlow } = useLease();
  const logger = useClientLogger();
  const STORE_STOCK_ELEMENT_PDP_CAMPAIGN_ID = 'a3fe6712-b594-4441-97f2-f0cddb991b4d';
  const formId = 'order-form';

  const { selectedBrand, selectedVariant, variantChoices, summary } =
    useContext(ProductTradeInContext);
  const {
    scrollTargetRefMainInformation,
    scrollTargetRefOrderSummary,
    isWithinScrollTargetMainInformation,
    isWithinScrollTargetOrderSummary
  } = useContext(OrderBlockContext);

  useEffect(() => {
    if (isWithinScrollTargetMainInformation) {
      setShowStickyAddToCart(true);
    }
  }, [isWithinScrollTargetMainInformation, showStickyAddToCart]);

  const isSummaryOrderBlock = orderBlockType === OrderBlockType.SUMMARY;
  const isDefaultOrderBlock = orderBlockType === OrderBlockType.DEFAULT;
  const isOutOfStockOrderBlock = orderBlockType === OrderBlockType.OUT_OF_STOCK;

  const showInlineWishlist = isOutOfStockOrderBlock && !showStorePickupButton;

  const showQuantityStepper =
    (isSummaryOrderBlock || isDefaultOrderBlock) &&
    isBusiness &&
    (!isLeasePage || (isLeasePage && !isLegacyLeaseFlow));

  const showRequestLeaseButton = isBusiness && isLeasePage && !isSecondChance;

  const isDigitalProduct = product?.productClass?.isDigital;

  const trackingEvent = () => {
    const params = {
      item_id: product.id,
      order_type: 'purchase' as const
    };

    trackEvent(
      isSummaryOrderBlock
        ? orderBlockSummaryAddToCartTrigger(params)
        : orderBlockAddToCartTrigger(params)
    );
  };

  const crossSellChargerTrackingEvent = (chargerId: string) => {
    const params = {
      primary_item_id: product.id,
      attached_item_ids: chargerId
    };

    trackEvent(crossSellChargerAddToCartTrigger(params));
  };

  const tradeIn = {
    state: summary?.priceLevel ?? '',
    optionId: selectedVariant,
    brandId: selectedBrand,
    selectedVarianceNames: variantChoices
  } as TradeInType;

  return (
    <ActiveMailerContextProvider>
      {storeStockElementPDPVariationId && (
        <TrackImmediate
          trigger={showSplitTestTrigger({
            test_name: STORE_STOCK_ELEMENT_PDP_CAMPAIGN_ID,
            variation_id: storeStockElementPDPVariationId
          })}
        />
      )}
      <Flex
        component='form'
        direction='column'
        gap={isSummaryOrderBlock ? 200 : 400}
        method='post'
        id={formId}
        ref={formRef}
        action={`/api/shopping-cart/add-item`}
        onSubmit={async event => {
          event.preventDefault();
          setIsLoading(true);

          const formElement = event.currentTarget;

          if (isPostalCodeCheckerImprovementSplitTestInVariation) {
            if (!(await getPostalCode())) {
              setIsLoading(false);
              setIsPostalCodeCheckerOpen(true);
              return;
            }
          }

          await onSubmitForm(
            formElement,
            tradeIn,
            trackingEvent,
            setIsLoading,
            product.id,
            quantity,
            logger,
            secondChanceProductId ? secondChanceProductId : undefined,
            secondChanceRedirectUrl,
            addToCartRedirectUrl,
            freeWithPromotion,
            { onChargerAdded: crossSellChargerTrackingEvent }
          );
        }}
      >
        <input type='hidden' name='productId' value={product.id} />

        {showQuantityStepper && (
          <QuantityStepper
            productType={product.productType.pluralName}
            productId={product.id}
            updateProductQuantity={setQuantity}
          />
        )}

        {children}

        <Flex direction='column' gap={isLeasePage ? 100 : 200}>
          <Flex
            id='scroll-target'
            gap={isLeasePage ? 100 : 200}
            ref={isSummaryOrderBlock ? scrollTargetRefOrderSummary : scrollTargetRefMainInformation}
            {...(isDefaultOrderBlock && !showOrderBlockATCButtonOnMobile && { hideUntil: 'md' })}
          >
            {showRequestLeaseButton ? (
              <RequestLeaseButton product={product as Product} quantity={quantity} />
            ) : (
              <OrderButton
                isLoading={isLoading}
                showWishlistButton={showInlineWishlist}
                product={product}
                privacyPolicyUrl={privacyPolicyUrl}
                feature={isSummaryOrderBlock ? 'order_summary' : 'order_block'}
                isInSinterklaasSplitTest={isInSinterklaasSplitTest}
                disabled={shouldDisableAddToCart}
                isCrossSellOnly={isCrossSellOnly}
                isSecondChance={isSecondChance}
                customerEmailAddress={customerEmailAddress}
              />
            )}
          </Flex>

          {!isPermanentlyOutOfStock && !showInlineWishlist && (
            <Flex gap={200}>
              {showStorePickupButton && !isLeasePage && !isSecondChance && !isDigitalProduct && (
                <StorePickupButton
                  product={product}
                  dateOverride={dateOverride}
                  isOrderSummary={isSummaryOrderBlock}
                  showAlternativePickUpButtonText={showAlternativePickUpButtonText}
                  preferredStore={preferredStore}
                />
              )}

              {!isLeasePage && (
                <WishlistButton
                  productId={product.id}
                  feature={isSummaryOrderBlock ? 'order_summary' : 'order_block'}
                  variant={showStorePickupButton && !isDigitalProduct ? 'compact' : 'default'}
                  isInSinterklaasSplitTest={isInSinterklaasSplitTest}
                />
              )}
            </Flex>
          )}

          {isDigitalProduct && <ReturnNotice />}
        </Flex>

        {isDefaultOrderBlock && tradeInComponent}

        {!isSummaryOrderBlock &&
          showStickyAddToCart &&
          !isWithinScrollTargetOrderSummary &&
          stickyBarSalesPrice && (
            <Portal containerId='sticky-add-to-cart-portal' renderInlineWhenMissing={true}>
              <StickyAddToCartBar
                product={product}
                propositions={stickyBarPropositions}
                availabilityState={availabilityState}
                isLoading={isLoading}
                privacyPolicyUrl={privacyPolicyUrl}
                stickyBarSalesPrice={stickyBarSalesPrice}
                isInSinterklaasSplitTest={isInSinterklaasSplitTest}
                shouldDisableAddToCart={shouldDisableAddToCart}
                isCrossSellOnly={isCrossSellOnly}
                isSecondChance={isSecondChance}
                showRequestLeaseButton={showRequestLeaseButton}
                quantity={quantity}
                formId={formId}
                showCompactATCButton={showCompactATCButton}
              />
            </Portal>
          )}
      </Flex>

      <ATCPostalCodeCheckerModal
        productType={product.productType.singularName}
        loginUrl={loginUrl}
        isLoggedIn={isLoggedIn}
        isOpen={isPostalCodeCheckerOpen}
        setIsOpen={setIsPostalCodeCheckerOpen}
        formRef={formRef as RefObject<HTMLFormElement>}
        productId={product.id}
      />
    </ActiveMailerContextProvider>
  );
};
